import { getGroupViewState, getOptionViewState, RENDER_MODE } from './view_state';
import {
  cloneTemplate,
  applyOptionState,
  applyGroupState,
  applySelectedOptionsState,
  createSubList,
  addNoResultsOptionIfNeeded,
} from './dom_handler';

export const createOptionRenderer = ({
  templates,
  multiple,
  fieldId,
  grouping = false,
  maxSelectedChoices = undefined,
  withDefaultOption = false,
  placeholder = '',
}) => {
  const _createSimpleOption = (optionData, context) => {
    const state = getOptionViewState(optionData, context);
    if (!state.shouldRender) {
      return null;
    }

    const optionElement = cloneTemplate(templates, 'option');
    applyOptionState(optionElement, state);

    return optionElement;
  };

  const _createOptionForMultipleSelection = (optionData, context) => {
    const state = getOptionViewState(optionData, context);
    if (!state.shouldRender) {
      return null;
    }

    const optionElement = cloneTemplate(templates, 'multiselectOption');
    applyOptionState(optionElement, state);

    const li = optionElement.querySelector('li');
    if (state.children.length > 0) {
      const subList = _addSelectableMultiLevelGroup(optionData, context);
      li.appendChild(subList);
    }

    return optionElement;
  };

  const _addSelectableMultiLevelGroup = (optionData, context) => {
    const subList = createSubList(optionData.display !== false);

    optionData.children.forEach((option) => {
      const optionElement = _addOption(option, context);
      if (optionElement) {
        subList.appendChild(optionElement);
      }
    });

    return subList;
  };

  const _groupFlatOptionsToTree = (options) => {
    const groups = new Map();

    options.forEach((option) => {
      if (!option.parent) {
        return;
      }

      const { id, label } = option.parent;

      if (!groups.has(id)) {
        groups.set(id, { id, label, children: [] });
      }

      groups.get(id).children.push(option);
    });

    return [...groups.values()].map((group) => ({
      ...group,
      display: group.children.some((child) => child.display !== false),
    }));
  };

  const _createOptionsGroup = (optionData, context) => {
    const state = getGroupViewState(optionData);

    const groupElement = cloneTemplate(templates, 'optionGroup');
    const groupTarget = groupElement.querySelector('.option-group__list');

    applyGroupState(groupElement, state);

    optionData.children.forEach((option) => {
      const optionElement = _addOption(option, context);
      if (optionElement) {
        groupTarget.appendChild(optionElement);
      }
    });

    return groupElement;
  };

  const _addOption = (optionData, context) => {
    if (context.grouping && optionData.children && optionData.children.length > 0) {
      return _createOptionsGroup(optionData, context);
    }

    if (multiple) {
      return _createOptionForMultipleSelection(optionData, context);
    }

    return _createSimpleOption(optionData, context);
  };

  const _addSelectedOptions = (optionData, context) => {
    const optionElement = cloneTemplate(templates, 'selectedOptions');
    const list = optionElement.querySelector('.selected-options__list');

    applySelectedOptionsState(optionElement, context.fieldId);

    const selectedContext = { ...context, renderMode: RENDER_MODE.ONLY_SELECTED };

    optionData.forEach((option) => {
      const childOptionElement = _addOption(option, selectedContext);
      if (childOptionElement) {
        list.appendChild(childOptionElement);
      }
    });

    return optionElement;
  };

  const _addDefaultOption = (fragment, selectedOptions, isSearching) => {
    if (!withDefaultOption || multiple || isSearching) {
      return;
    }

    const defaultOptionElement = cloneTemplate(templates, 'option');
    applyOptionState(defaultOptionElement, {
      id: '',
      label: placeholder,
      isSelected: selectedOptions.length === 0,
      isVisible: true,
    });
    fragment.appendChild(defaultOptionElement);
  };

  const addOptions = ({ options, target, selectedOptions, isSearching = false, isSearchable = false }) => {
    const fragment = document.createDocumentFragment();
    const shouldRenderSelectedOptions = isSearchable && multiple && selectedOptions.length && !isSearching;

    const context = {
      multiple,
      maxSelectedChoices,
      isSearching,
      fieldId,
      grouping,
      selectedIds: new Set(selectedOptions.map((option) => option.id)),
      selectedCount: selectedOptions.length,
      renderMode: shouldRenderSelectedOptions ? RENDER_MODE.ONLY_UNSELECTED : RENDER_MODE.ALL,
    };

    const renderableOptions = grouping ? _groupFlatOptionsToTree(options) : options;

    _addDefaultOption(fragment, selectedOptions, isSearching);

    if (shouldRenderSelectedOptions) {
      const selectedOptionsFragment = _addSelectedOptions(renderableOptions, context);
      if (selectedOptionsFragment) {
        fragment.appendChild(selectedOptionsFragment);
      }
    }

    renderableOptions.forEach((option) => {
      const optionElement = _addOption(option, context);
      if (optionElement) {
        fragment.appendChild(optionElement);
      }
    });

    addNoResultsOptionIfNeeded(fragment, options, isSearching);

    target.appendChild(fragment);
  };

  const showLoading = (target) => {
    const loadingElement = cloneTemplate(templates, 'loading');
    target.appendChild(loadingElement);
  };

  return {
    addOptions,
    showLoading,
  };
};
